import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage } from "@/lib/api";
import { useDeliveryUpdater } from "@/lib/queries";
import { Callout } from "@/components/common/Blocks";

export const ReleaseChecklist = ({ checks, setChecks, productionStarted, ack, setAck }) => (
  <div className="space-y-2">
    <ul className="divide-y border-y">
      {[
        ["spec", "Spesifikasjon kontrollert mot kundegodkjent grunnlag"],
        ["drawing", "Gjeldende tegning stemmer med spesifikasjonen"],
        ["measurements", "Mål kontrollert mot måleskjema"],
      ].map(([k, label]) => (
        <li key={k}>
          <label className="flex min-h-[44px] cursor-pointer items-center gap-3 py-2 text-[14.5px]" data-testid={`release-check-${k}`}>
            <Checkbox checked={!!checks[k]} onCheckedChange={(v) => setChecks({ ...checks, [k]: !!v })} />
            <span className={checks[k] ? "font-medium" : ""}>{label}</span>
          </label>
        </li>
      ))}
    </ul>
    {productionStarted && (
      <Callout tone="waiting" title="Produksjonen er startet" testId="release-production-warning" className="mt-3">
        En statusendring her stopper ingen maskin. Produksjonsansvarlig må bekrefte at endringen kan gjennomføres.
        <Label htmlFor="ack" className="mt-3 block text-[13px]">Bekreftet av (produksjon)</Label>
        <Input id="ack" value={ack} onChange={(e) => setAck(e.target.value)} placeholder="Navn" className="mt-1" data-testid="release-production-ack-input" />
      </Callout>
    )}
  </div>
);

export const ReleaseDialog = ({ open, onOpenChange, delivery, changeId, title = "Intern teknisk kontroll og frigivelse" }) => {
  const update = useDeliveryUpdater();
  const [checks, setChecks] = useState({});
  const [by, setBy] = useState(delivery?.technical_responsible || "Sindre Tveit");
  const [note, setNote] = useState("");
  const [ack, setAck] = useState("");
  const [busy, setBusy] = useState(false);
  const prodStarted = ["startet", "ferdig"].includes(delivery?.production?.status);
  const allChecked = checks.spec && checks.drawing && checks.measurements && (!prodStarted || ack.trim());

  const submit = async () => {
    setBusy(true);
    try {
      const url = changeId ? `/deliveries/${delivery.id}/changes/${changeId}/release` : `/deliveries/${delivery.id}/release`;
      const { data } = await api.post(url, { reviewed_by: by, note, checks, production_ack_by: ack || null });
      update(data);
      toast.success("Grunnlaget er frigitt", { description: "Ny revisjon er gjeldende. Eldre revisjoner er beholdt i historikken." });
      onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Kundegodkjenning gjør ikke grunnlaget produksjonsklart. Frigivelsen er en egen, intern handling.</DialogDescription>
        </DialogHeader>
        <ReleaseChecklist checks={checks} setChecks={setChecks} productionStarted={prodStarted} ack={ack} setAck={setAck} />
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5"><Label htmlFor="rel-by">Kontrollert av</Label><Input id="rel-by" value={by} onChange={(e) => setBy(e.target.value)} data-testid="release-reviewed-by" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="rel-note">Merknad</Label><Textarea id="rel-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="release-note" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={submit} disabled={!allChecked || busy} data-testid="internal-release-confirm-button">{busy && <Loader2 className="animate-spin" />} Frigi grunnlaget</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SendApprovalDialog = ({ open, onOpenChange, delivery, changeId, defaultMessage }) => {
  const update = useDeliveryUpdater();
  const [recipient, setRecipient] = useState(delivery?.roles?.orderer?.name || delivery?.customer_name || "");
  const [message, setMessage] = useState(defaultMessage || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const url = changeId ? `/deliveries/${delivery.id}/changes/${changeId}/send-approval` : `/deliveries/${delivery.id}/approvals`;
      const { data } = await api.post(url, { recipient, message_to_customer: message, sent_by: delivery.responsible });
      update(data);
      toast.success("Sendt til kundegodkjenning (simulert)", { description: "Ingen e-post er sendt. Åpne kundevisningen for å svare som kunde." });
      onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send til kundegodkjenning</DialogTitle>
          <DialogDescription>Kunden får en begrenset kundevisning med tegning, spesifikasjon og endringer. I prototypen sendes ingen e-post – du åpner visningen selv.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label htmlFor="ap-rec">Mottaker</Label><Input id="ap-rec" value={recipient} onChange={(e) => setRecipient(e.target.value)} data-testid="approval-recipient-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="ap-msg">Melding til kunden</Label><Textarea id="ap-msg" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="approval-message-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={submit} disabled={busy} data-testid="approval-send-button">{busy && <Loader2 className="animate-spin" />} Send til kundegodkjenning</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AddMessageDialog = ({ open, onOpenChange, delivery }) => {
  const update = useDeliveryUpdater();
  const [direction, setDirection] = useState("inn");
  const [sender, setSender] = useState(delivery?.roles?.orderer?.name || delivery?.customer_name || "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/deliveries/${delivery.id}/messages`, { direction, channel: direction === "intern" ? "notat" : "e-post", sender: direction === "inn" ? sender : delivery.responsible, subject, body });
      update(data);
      toast.success(direction === "inn" ? "Kundebeskjed registrert" : "Notat lagret");
      onOpenChange(false);
      setBody("");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Registrer melding</DialogTitle>
          <DialogDescription>Simuler en ny beskjed fra kunden, eller legg inn et internt notat. Ingenting sendes ut.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger data-testid="message-direction-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="inn">Beskjed fra kunde</SelectItem><SelectItem value="intern">Internt notat</SelectItem></SelectContent>
            </Select>
          </div>
          {direction === "inn" && <div className="space-y-1.5"><Label htmlFor="msg-sender">Avsender</Label><Input id="msg-sender" value={sender} onChange={(e) => setSender(e.target.value)} /></div>}
          <div className="space-y-1.5"><Label htmlFor="msg-subject">Emne</Label><Input id="msg-subject" value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
          <div className="space-y-1.5"><Label htmlFor="msg-body">Innhold</Label><Textarea id="msg-body" rows={5} value={body} onChange={(e) => setBody(e.target.value)} data-testid="message-body-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={submit} disabled={busy || !body.trim()} data-testid="message-save-button">Lagre</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
